import React, { useState } from 'react';
import PropTypes from 'prop-types';

import {
  Box,
  IconButton,
  TextField,
  Typography
} from '@mui/material';

import {
  AddOutlined,
  CheckOutlined,
  ClearOutlined,
  DeleteOutlined
} from '@mui/icons-material';

import {
  panelSx,
  panelHeaderSx,
  panelTitleSx,
  searchRowSx,
  addRowSx,
  diagnosisListSx,
  diagnosisRowSx,
  diagnosisTextSx,
  iconButtonSx,
  searchFieldSx,
  codeFieldSx,
  descriptionFieldSx
} from '../Styles';

const UserDiagnosisPanel = ({
  diagnoses,
  searchValue,
  onSearchChange,
  onAddDiagnosis,
  onDelete
}) => {
  const [isAddOpen, setIsAddOpen] = useState(false);

  const [code, setCode] = useState('');
  const [description, setDescription] = useState('');

  const handleAdd = () => {
    if (
      !code.trim() ||
      !description.trim()
    ) {
      return;
    }

    onAddDiagnosis({
      code: code.trim(),
      description: description.trim()
    });

    setCode('');
    setDescription('');
    setIsAddOpen(false);
  };

  const handleClear = () => {
    setCode('');
    setDescription('');
    setIsAddOpen(false);
  };

  const handleToggleAdd = () => {
    setIsAddOpen(previous => !previous);

    if (isAddOpen) {
      setCode('');
      setDescription('');
    }
  };

  return (
    <Box sx={panelSx}>
      <Box sx={panelHeaderSx}>
        <Typography sx={panelTitleSx}>
          User Defined ICD &amp; Descriptions
        </Typography>
      </Box>

      {/* Search */}
      <Box sx={searchRowSx}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search user codes and descriptions"
          value={searchValue}
          onChange={event =>
            onSearchChange(event.target.value)
          }
          sx={searchFieldSx}
        />

        <IconButton
          size="small"
          aria-label="Add ICD"
          onClick={handleToggleAdd}
          sx={iconButtonSx}
        >
          <AddOutlined
            sx={{
              fontSize: 18
            }}
          />
        </IconButton>
      </Box>

      {/* Show only after clicking + */}
      {isAddOpen && (
        <Box sx={addRowSx}>
          <TextField
            autoFocus
            size="small"
            placeholder="Input Code"
            value={code}
            onChange={event =>
              setCode(event.target.value)
            }
            sx={codeFieldSx}
          />

          <TextField
            fullWidth
            size="small"
            placeholder="Input Description"
            value={description}
            onChange={event =>
              setDescription(event.target.value)
            }
            sx={descriptionFieldSx}
          />

          <IconButton
            size="small"
            disabled={
              !code.trim() ||
              !description.trim()
            }
            onClick={handleAdd}
            sx={{
              ...iconButtonSx,
              color: '#4CAF50'
            }}
          >
            <CheckOutlined
              sx={{
                fontSize: 18
              }}
            />
          </IconButton>

          <IconButton
            size="small"
            onClick={handleClear}
            sx={{
              ...iconButtonSx,
              color: '#EF5350'
            }}
          >
            <ClearOutlined
              sx={{
                fontSize: 18
              }}
            />
          </IconButton>
        </Box>
      )}

      {/* List */}
      <Box sx={diagnosisListSx}>
        {diagnoses.length > 0 ? (
          diagnoses.map(diagnosis => (
            <Box
              key={diagnosis.id}
              sx={diagnosisRowSx}
            >
              <Typography
                title={`${diagnosis.code} ${diagnosis.description}`}
                sx={diagnosisTextSx}
              >
                {diagnosis.code}{' '}
                {diagnosis.description}
              </Typography>

              <IconButton
                size="small"
                aria-label={`Delete ${diagnosis.code}`}
                onClick={() =>
                  onDelete(diagnosis.id)
                }
                sx={iconButtonSx}
              >
                <DeleteOutlined
                  sx={{
                    fontSize: 17
                  }}
                />
              </IconButton>
            </Box>
          ))
        ) : (
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Typography
              sx={{
                fontSize: 13,
                color: '#757575'
              }}
            >
              No ICD10 records available
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

UserDiagnosisPanel.propTypes = {
  diagnoses: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string
      ]).isRequired,
      code: PropTypes.string.isRequired,
      description:
        PropTypes.string.isRequired
    })
  ),
  searchValue: PropTypes.string,
  onSearchChange: PropTypes.func,
  onAddDiagnosis: PropTypes.func,
  onDelete: PropTypes.func
};

UserDiagnosisPanel.defaultProps = {
  diagnoses: [],
  searchValue: '',
  onSearchChange: () => {},
  onAddDiagnosis: () => {},
  onDelete: () => {}
};

export default UserDiagnosisPanel;